import { Box, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import { CellWithSecondaryText } from "components/DataGrid";
import { ExportDataButton } from "components/ExportDataButton";
import { FilterGroup } from "components/Omnisearch/FilterPopover";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import CopyableTypography from "components/Typography/CopyableTypography";
import { STATE_NAMES } from "encamp-shared/src/constants/states";
import { formatAddress } from "encamp-shared/src/utils/address";
import { federalWasteGeneratorStatusToLabel } from "encamp-shared/src/waste/index";
import { gql } from "generated-graphql";
import {
  RcraInfoFederalWasteGeneratorStatus,
  WasteFacilitiesQuery,
} from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useTenant } from "hooks/useTenant";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { stateOptionLabel } from "util/filterOptionLabels";

const PREFERRED_GENERATOR_STATUS_ORDER = [
  RcraInfoFederalWasteGeneratorStatus.VerySmallQuantityGenerator,
  RcraInfoFederalWasteGeneratorStatus.SmallQuantityGenerator,
  RcraInfoFederalWasteGeneratorStatus.LargeQuantityGenerator,
  RcraInfoFederalWasteGeneratorStatus.Pending,
  RcraInfoFederalWasteGeneratorStatus.NotAGenerator,
  RcraInfoFederalWasteGeneratorStatus.Undetermined,
];

const SORTED_WASTE_GENERATOR_STATUSES = Object.values(RcraInfoFederalWasteGeneratorStatus)
  .sort((a, b) => {
    const aIndex = PREFERRED_GENERATOR_STATUS_ORDER.indexOf(a);
    const bIndex = PREFERRED_GENERATOR_STATUS_ORDER.indexOf(b);
    return aIndex - bIndex;
  })
  .concat("None" as unknown as RcraInfoFederalWasteGeneratorStatus);

const WASTE_FACILITIES = gql(`
  query WasteFacilities(
    $search: String
    $page: Int
    $pageSize: Int
    $sort: [SortModel!]
  ) {
    wasteFacilities(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        facility {
          id
          name
          customerFacilityId
          streetAddress1
          city
          state
          zip
          facilityAlternateIds {
            id
            type
            value
            hasNoId
          }
        }
        epaId
        reportedWasteGeneratorStatus
        calculatedWasteGeneratorStatus
      }
      count
    }
  }
`);

export const WasteFacilities = () => {
  const navigate = useNavigate();
  const { tenantId } = useTenant();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useBreadcrumb([
    {
      label: "Waste",
      to: tenantId ? `/o/${tenantId}/waste` : undefined,
    },
    {
      label: "Facilities",
    },
  ]);

  const columns: OmnisearchGridColDef<WasteFacilitiesQuery["wasteFacilities"]["items"][number]>[] =
    [
      {
        field: "epaId",
        headerName: "EPA ID",
        minWidth: 150,
        sortable: true,
        renderCell: ({ value }) =>
          value ? <CopyableTypography text={value} variant="body2" /> : null,
      },
      {
        field: "name",
        headerName: "Facility",
        filterKeyType: "wasteFacility",
        renderCell: (params) => (
          <CellWithSecondaryText
            primaryValue={
              params.row.facility.customerFacilityId
                ? `${params.row.facility.name} (${params.row.facility.customerFacilityId})`
                : params.row.facility.name
            }
            secondaryValue={formatAddress(
              params.row.facility.streetAddress1,
              params.row.facility.city,
              params.row.facility.state,
              params.row.facility.zip
            )}
          />
        ),
        flex: 1,
      },
      {
        field: "reportedWasteGeneratorStatus",
        headerName: "EPA Current Status",
        minWidth: 185,
        flex: 0.4,
        sortable: true,
        headerClassName: "wrapHeader",
        filterKeyType: "enumAutocomplete",
        enumValues: SORTED_WASTE_GENERATOR_STATUSES,
        valueGetter(_, row) {
          return federalWasteGeneratorStatusToLabel(
            row.reportedWasteGeneratorStatus ?? RcraInfoFederalWasteGeneratorStatus.Undetermined
          );
        },
        renderCell({ value }) {
          return (
            <Tooltip title="Federal waste generator status as currently listed with the EPA.">
              {value}
            </Tooltip>
          );
        },
      },
      {
        field: "calculatedWasteGeneratorStatus",
        headerName: "Calculated Generator Status (YTD)",
        minWidth: 185,
        flex: 0.4,
        sortable: true,
        headerClassName: "wrapHeader",
        filterKeyType: "enumAutocomplete",
        enumValues: SORTED_WASTE_GENERATOR_STATUSES,
        valueGetter(_, row) {
          return federalWasteGeneratorStatusToLabel(
            row.calculatedWasteGeneratorStatus ?? RcraInfoFederalWasteGeneratorStatus.Undetermined
          );
        },
        renderCell({ value }) {
          return (
            <Tooltip title="Highest calculated federal waste generator status from vendor data this calendar year.">
              {value}
            </Tooltip>
          );
        },
      },
    ];

  const renderCommandButtons = ({ search }: { search: string }) => [
    <ExportDataButton
      key="export"
      tenantId={tenantId ?? ""}
      exportType="wasteFacilities"
      fileName="waste-facilities"
      filter={search ? { search } : undefined}
      iconOnly
    />,
  ];

  const filterGroups: FilterGroup[] = useMemo(
    () => [
      {
        name: "Facility Info",
        fields: ["name", "epaId", "customerFacilityId"],
      },
      {
        name: "Location",
        fields: ["streetAddress1", "city", "state", "zip"],
      },
      {
        name: "Status",
        fields: ["reportedWasteGeneratorStatus", "calculatedWasteGeneratorStatus"],
      },
    ],
    []
  );

  const defaultSearch = `tenantId:${tenantId}`;

  return (
    <Box sx={{ py: 3 }}>
      <OmnisearchDataGrid
        persistenceKey="waste-facilities-table"
        isURLDriven
        columns={columns}
        dataQuery={WASTE_FACILITIES}
        getRowId={(row) => `${row.facility.id}-${row.epaId ?? "no-epaId"}`}
        getItems={(data) => data.wasteFacilities.items}
        getCount={(data) => data.wasteFacilities.count}
        noDataMessage={"No waste facilities have been added to this organization"}
        isRowSelectable={() => false}
        defaultSearch={defaultSearch}
        initialSortModel={[{ field: "name", sort: "asc" }]}
        rowHeight={"auto"}
        commandButtons={renderCommandButtons}
        additionalFilterColumns={[
          { header: "Facility ID", key: "customerFacilityId" },
          { header: "Street Address", key: "streetAddress1" },
          { header: "City", key: "city" },
          {
            header: "State/Territory",
            key: "state",
            filterKeyType: "enumAutocomplete",
            enumValues: Object.keys(STATE_NAMES),
            enumPresentationFunction: stateOptionLabel,
          },
          { header: "Zip", key: "zip" },
        ]}
        onRowClick={(params) => navigate(`${params.row.facility.id}`)}
        getRowLink={(row) => `./${row.facility.id}`}
        filterGroups={filterGroups}
        columnVisibilityModel={{
          ...(isMobile && {
            epaId: false,
            reportedWasteGeneratorStatus: false,
            calculatedWasteGeneratorStatus: false,
          }),
        }}
      />
    </Box>
  );
};
